'use client';

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Image from 'next/image';
import Link from 'next/link';
import { AppIcon } from '@impacta/shared';
import { useCart } from '@/contexts/CartContext';
import { formatCLP } from '@/lib/format';
import type { ShopifyCartLine } from '@/lib/shopify/types/cart';
import { TIERS, matchTierKey } from '@impacta/shared';
import GiftBoxIcon from '@/components/GiftBoxIcon';

function flattenLines(edges: { node: ShopifyCartLine }[]): ShopifyCartLine[] {
  return edges.map((e) => e.node);
}

export default function CartDrawer() {
  const { cart, loading, isDrawerOpen, closeDrawer, removeItem, updateQuantity, checkoutUrl } =
    useCart();

  const panelRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  // Close on Escape key
  useEffect(() => {
    if (!isDrawerOpen) return;

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeDrawer();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isDrawerOpen, closeDrawer]);

  // Trap focus inside drawer when open
  useEffect(() => {
    if (!isDrawerOpen) return;

    const panel = panelRef.current;
    if (!panel) return;

    const focusable = panel.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length > 0) focusable[0].focus();

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleTab);
    return () => document.removeEventListener('keydown', handleTab);
  }, [isDrawerOpen]);

  // Prevent body scroll without layout shift.
  // scrollbar-gutter:stable reserves the scrollbar lane even when hidden,
  // so removing the scrollbar never causes a reflow in the header or body.
  useEffect(() => {
    const html = document.documentElement;
    if (isDrawerOpen) {
      html.style.overflow = 'hidden';
      html.style.scrollbarGutter = 'stable';
    } else {
      html.style.overflow = '';
      html.style.scrollbarGutter = '';
    }
    return () => {
      html.style.overflow = '';
      html.style.scrollbarGutter = '';
    };
  }, [isDrawerOpen]);

  const lines = cart?.lines?.edges ? flattenLines(cart.lines.edges) : [];
  const isEmpty = lines.length === 0;

  if (!mounted) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-[51] bg-black/40 transition-opacity duration-300 ${
          isDrawerOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={closeDrawer}
        aria-hidden="true"
      />

      {/* Drawer panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Tu carrito"
        className={`fixed top-0 right-0 z-[52] h-screen w-full max-w-md bg-white shadow-xl flex flex-col transition-transform duration-300 ease-in-out ${
          isDrawerOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E8E8E4]">
          <h2 className="text-lg font-bold text-[#373A36]">Tu carrito</h2>
          <button
            onClick={closeDrawer}
            className="p-2 rounded-full text-[#6b6e68] hover:text-[#373A36] hover:bg-[#F5F5F3] transition-colors"
            aria-label="Cerrar carrito"
          >
            <AppIcon name="XMarkIcon" size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading && isEmpty && (
            <div className="flex items-center justify-center py-12 text-sm text-[#6b6e68]">
              Cargando...
            </div>
          )}

          {!loading && isEmpty && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AppIcon name="ShoppingBagIcon" size={48} className="text-[#E8E8E4] mb-4" />
              <p className="text-sm text-[#6b6e68] mb-4">Tu carrito esta vacio</p>
              <Link
                href="/actividades"
                onClick={closeDrawer}
                className="text-sm font-semibold text-[#00A19B] hover:underline"
              >
                Explorar actividades
              </Link>
            </div>
          )}

          {!isEmpty && (
            <ul className="space-y-4">
              {lines.map((line) => {
                const product = line.merchandise.product;
                const image = product.images.edges[0]?.node;
                const isGiftCardProduct =
                  product.title.toLowerCase().includes('tarjeta regalo') ||
                  product.title.toLowerCase().includes('gift card');
                const giftRecipientName = line.attributes?.find(
                  (a) => a.key === '_recipient_name'
                )?.value;
                const recipientName = line.attributes?.find(
                  (a) => a.key === 'Recipient Name'
                )?.value;

                return (
                  <li
                    key={line.id}
                    className="flex gap-3 p-3 rounded-2xl border border-[#E8E8E4] bg-white"
                  >
                    {/* Product image or gift card animated thumbnail */}
                    {isGiftCardProduct ? (
                      <div
                        className="flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden relative flex items-center justify-center"
                        style={{ background: TIERS[matchTierKey(line.merchandise.title)].gradient }}
                      >
                        <GiftBoxIcon className="w-14 h-14 animate-gift-box" />
                      </div>
                    ) : image ? (
                      <div className="flex-shrink-0 w-16 h-16 rounded-xl overflow-hidden bg-[#F5F5F3]">
                        <Image
                          src={image.url}
                          alt={image.altText ?? product.title}
                          width={64}
                          height={64}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : null}

                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#373A36] truncate">
                        {product.title}
                      </p>
                      <p className="text-xs text-[#6b6e68] truncate">{line.merchandise.title}</p>
                      {recipientName && (
                        <div className="mt-0.5">
                          <p className="text-xs text-[#00A19B]">Para: {recipientName}</p>
                        </div>
                      )}
                      {giftRecipientName && !recipientName && (
                        <div className="mt-0.5 flex items-center gap-1">
                          <AppIcon name="GiftIcon" size={12} className="text-[#00A19B]" />
                          <p className="text-xs font-semibold text-[#00A19B]">
                            Regalo para {giftRecipientName}
                          </p>
                        </div>
                      )}
                      <p className="text-sm font-bold text-[#373A36] mt-1">
                        {formatCLP(line.cost.totalAmount.amount)}
                      </p>

                      {!isGiftCardProduct && !line.merchandise.availableForSale && (
                        <p className="text-xs text-red-500 mt-1 font-medium">
                          ⚠️ Sin stock disponible
                        </p>
                      )}
                      {!isGiftCardProduct &&
                        line.merchandise.availableForSale &&
                        line.merchandise.quantityAvailable !== null &&
                        line.merchandise.quantityAvailable > 0 &&
                        line.merchandise.quantityAvailable <= 5 && (
                          <p className="text-xs text-orange-500 mt-1">
                            ¡Solo quedan {line.merchandise.quantityAvailable} cupos!
                          </p>
                        )}
                      <div className="flex items-center gap-2 mt-2">
                        <button
                          onClick={() =>
                            line.quantity === 1
                              ? removeItem(line.id)
                              : updateQuantity(line.id, line.quantity - 1)
                          }
                          disabled={loading}
                          className="w-7 h-7 flex items-center justify-center rounded-lg border border-[#E8E8E4] text-[#6b6e68] hover:border-[#00A19B] hover:text-[#00A19B] transition-colors disabled:opacity-50"
                          aria-label="Disminuir cantidad"
                        >
                          <AppIcon name="MinusIcon" size={14} />
                        </button>
                        <span className="text-sm font-semibold text-[#373A36] min-w-[20px] text-center">
                          {line.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(line.id, line.quantity + 1)}
                          disabled={
                            loading ||
                            (!isGiftCardProduct &&
                              !!line.merchandise.quantityAvailable &&
                              line.quantity >= line.merchandise.quantityAvailable)
                          }
                          className="w-7 h-7 flex items-center justify-center rounded-lg border border-[#E8E8E4] text-[#6b6e68] hover:border-[#00A19B] hover:text-[#00A19B] transition-colors disabled:opacity-50"
                          aria-label="Aumentar cantidad"
                        >
                          <AppIcon name="PlusIcon" size={14} />
                        </button>

                        <button
                          onClick={() => removeItem(line.id)}
                          disabled={loading}
                          className="ml-auto p-1 rounded-lg text-[#6b6e68] hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50"
                          aria-label="Eliminar del carrito"
                        >
                          <AppIcon name="TrashIcon" size={16} />
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {loading && !isEmpty && (
            <div className="mt-3 text-center text-xs text-[#6b6e68]">Actualizando...</div>
          )}
        </div>

        {!isEmpty && (
          <div
            className="border-t border-[#E8E8E4] px-6 py-4 space-y-3"
            aria-live="polite"
            aria-atomic="false"
          >
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-[#6b6e68]">Subtotal</span>
              <span className="text-lg font-extrabold text-[#373A36]">
                {cart ? formatCLP(cart.cost.subtotalAmount.amount) : '--'}
              </span>
            </div>
            <button
              onClick={() => {
                if (checkoutUrl) window.location.href = checkoutUrl;
              }}
              disabled={
                loading || !checkoutUrl || lines.some((line) => !line.merchandise.availableForSale)
              }
              className="w-full flex items-center justify-center gap-2 py-4 rounded-full font-bold text-base transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: '#E0DF00', color: '#373A36' }}
            >
              <AppIcon name="CreditCardIcon" size={18} />
              {lines.some((line) => !line.merchandise.availableForSale)
                ? 'Productos sin stock'
                : 'Ir a pagar'}
            </button>
          </div>
        )}
      </div>
    </>,
    document.body
  );
}
